import { useEffect, useRef } from "react";

/**
 * Logs out the user after a period of inactivity.
 *
 * Notes:
 * - This is an app-side idle timeout only. It does not control Entra ID token lifetimes.
 * - It resets on common user/input events.
 */
export function useIdleLogout(options: {
    timeoutMs: number;
    onIdle: () => void | Promise<void>;
    enabled?: boolean;
    onError?: (err: unknown) => void;
}) {
    const { timeoutMs, onIdle, enabled = true, onError } = options;

    const timerIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const onIdleRef = useRef(onIdle);
    onIdleRef.current = onIdle;

    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;

    useEffect(() => {
        if (!enabled) return;

        const reportIdleError = (err: unknown) => {
            // Avoid unhandled promise rejection noise if onIdle is async and fails
            if (onErrorRef.current) {
                onErrorRef.current(err);
                return;
            }
            // eslint-disable-next-line no-console
            console.error("Idle logout failed", err);
        };

        const invokeOnIdle = () => {
            Promise.resolve(onIdleRef.current()).catch(reportIdleError);
        };

        const reset: EventListener = () => {
            if (timerIdRef.current) {
                clearTimeout(timerIdRef.current);
            }
            timerIdRef.current = setTimeout(invokeOnIdle, timeoutMs);
        };

        const events: Array<keyof WindowEventMap> = [
            "mousemove",
            "mousedown",
            "keydown",
            "scroll",
            "touchstart",
            "wheel",
            "focus"
        ];

        const listenerOptions: AddEventListenerOptions = { passive: true };

        events.forEach((e) => window.addEventListener(e, reset, listenerOptions));

        // start timer immediately
        reset(new Event("init"));

        return () => {
            if (timerIdRef.current) {
                clearTimeout(timerIdRef.current);
                timerIdRef.current = null;
            }
            events.forEach((e) => window.removeEventListener(e, reset, listenerOptions));
        };
    }, [enabled, timeoutMs]);
}
